import { useAppDispatch } from '@/app/hooks';
import EtablissementAutocomplete from '@/components/Etablissement/EtablissementAutocomplete';
import {
  useCreateAffectationMutation,
  useGetAffectationQuery,
  useUpdateAffectationMutation,
} from '@/features/api/endpoints/affectationEndpoints';
import { useGetRolesQuery } from '@/features/api/endpoints/roleEndpoints';
import { closeDrawer } from '@/features/drawer/drawerSlice';
import AffectationFormDrawerLoader from '@/features/drawer/drawers/AffectationFormDrawerLoader';
import { useGetCurrentUser } from '@/shared/auth/useDirectusUser';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Grid, Select, Stack } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslate } from '@tolgee/react';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import { useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

// ─── Types ────────────────────────────────────────────────────────────────────

type Props = {
  payload?: { affectationId: number };
};

const formSchema = z.object({
  role: z.number(),
  etablissement: z.string().optional(),
  status: z.enum(['draft', 'published', 'archived']),
});

type EditFormValues = z.infer<typeof formSchema>;

const extractId = (val: number | { id: number } | null | undefined): number | undefined => {
  if (val === null || val === undefined) return undefined;
  if (typeof val === 'number') return val;
  return val.id;
};

const AffectationFormDrawer = ({ payload }: Props) => {
  const { t } = useTranslate();
  const dispatch = useAppDispatch();
  const { user } = useGetCurrentUser();

  const isEdit = payload?.affectationId !== undefined;

  const { data: affectation, isLoading: loadingAffectation } = useGetAffectationQuery(
    payload?.affectationId as number,
    {
      skip: !isEdit,
    },
  );

  const { data: roles = [], isLoading: loadingRoles } = useGetRolesQuery({
    sort: ['name'],
  });

  const [createAffectation] = useCreateAffectationMutation();
  const [updateAffectation] = useUpdateAffectationMutation();

  const {
    control,
    clearErrors,
    handleSubmit,
    reset,
    setError,
    formState: { isSubmitting, isDirty },
  } = useForm<EditFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      role: undefined as unknown as number,
      etablissement: undefined as string | undefined,
      status: 'draft',
    },
  });

  useEffect(() => {
    if (isEdit && affectation) {
      reset({
        role: extractId(affectation.role),
        etablissement: isObject(affectation.etablissement)
          ? affectation.etablissement.finess
          : isString(affectation.etablissement)
            ? affectation.etablissement
            : undefined,
        status: affectation.status,
      });
    }
  }, [affectation, isEdit, reset]);

  const roleOptions = roles.map(r => ({ value: String(r.id), label: r.name ?? String(r.id) }));

  const selectedRoleId = useWatch({
    control,
    name: 'role',
  });

  const etablissementRequired =
    roles.find(role => role.id === selectedRoleId)?.etablissementObligatoire === true;

  const statusOptions = [
    { value: 'draft', label: t('profil.affectations.status.draft') },
    { value: 'published', label: t('profil.affectations.status.published') },
    { value: 'archived', label: t('profil.affectations.status.archived') },
  ];

  const onSubmit = async (data: EditFormValues) => {
    clearErrors('etablissement');

    if (etablissementRequired && !data.etablissement?.trim()) {
      setError('etablissement', {
        type: 'manual',
        message: 'required',
      });
      return;
    }

    try {
      if (isEdit && payload) {
        await updateAffectation({
          id: payload.affectationId,
          body: {
            role: data.role,
            etablissement: data.etablissement,
            status: data.status,
          },
        }).unwrap();
      } else {
        if (!user?.id) {
          return;
        }
        await createAffectation({
          role: data.role,
          etablissement: data.etablissement,
          user: user.id,
          status: 'draft',
        }).unwrap();
      }
      dispatch(closeDrawer());
      notifications.show({
        color: 'green',
        message: isEdit
          ? t('profil.affectations.form.success.update')
          : t('profil.affectations.form.success.submit'),
      });
    } catch {
      notifications.show({
        color: 'red',
        message: t('profil.affectations.form.error.submit'),
      });
    }
  };

  if (loadingAffectation || loadingRoles) {
    return <AffectationFormDrawerLoader isEdit={isEdit} />;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack gap="md">
        <Grid>
          <Grid.Col span={12}>
            <Controller
              control={control}
              name="role"
              render={({ field: f, fieldState }) => (
                <Select
                  label={t('onboarding.form.role')}
                  placeholder={t('profil.affectations.form.role.placeholder')}
                  data={roleOptions}
                  value={f.value ? String(f.value) : null}
                  onChange={val => {
                    const nextRole = val !== null ? Number(val) : undefined;
                    f.onChange(nextRole);

                    const nextRoleRequiresEtablissement =
                      roles.find(role => role.id === nextRole)?.etablissementObligatoire === true;

                    if (!nextRoleRequiresEtablissement) {
                      clearErrors('etablissement');
                    }
                  }}
                  error={
                    fieldState.error ? t('profil.affectations.form.error.required') : undefined
                  }
                  disabled={isSubmitting}
                  required
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <Controller
              control={control}
              name="etablissement"
              render={({ field: f, fieldState }) => (
                <EtablissementAutocomplete
                  label={t('onboarding.form.etablissement')}
                  placeholder={t('profil.affectations.form.etablissement.placeholder')}
                  value={f.value ?? null}
                  onChange={val => f.onChange(val ?? undefined)}
                  error={
                    fieldState.error ? t('profil.affectations.form.error.required') : undefined
                  }
                  disabled={isSubmitting}
                  required={etablissementRequired}
                />
              )}
            />
          </Grid.Col>
          {isEdit && (
            <Grid.Col span={12}>
              <Controller
                control={control}
                name="status"
                render={({ field: f }) => (
                  <Select
                    label={t('profil.affectations.columns.status')}
                    data={statusOptions}
                    value={f.value}
                    onChange={val =>
                      f.onChange((val ?? 'draft') as 'draft' | 'published' | 'archived')
                    }
                    disabled={isSubmitting}
                    required
                  />
                )}
              />
            </Grid.Col>
          )}
        </Grid>
        <Button type="submit" loading={isSubmitting} disabled={!isDirty}>
          {isEdit ? t('profil.affectations.form.update') : t('profil.affectations.form.submit')}
        </Button>
      </Stack>
    </form>
  );
};

export default AffectationFormDrawer;
